import { useMemo } from "react";
import dayjs from "dayjs";
import { useGetMissionControlTenantShredUsageQuery } from "src/store/api/generatedApi";

export interface ShredUsageDataPoint {
  value: number;
  label: string;
}

export const SHRED_USAGE_WARNING_THRESHOLD = 40;

export const formatShredUsage = (value: number) => `${value.toFixed(1)}%`;

interface ShredUsagePoint {
  month: string;
  mix_percentage: number;
}

const aggregateShredUsage = (
  allSeries: ShredUsagePoint[][]
): ShredUsagePoint[] => {
  const byMonth = new Map<string, { total: number; count: number }>();

  allSeries.flat().forEach((point) => {
    const existing = byMonth.get(point.month);
    if (!existing) {
      byMonth.set(point.month, { total: point.mix_percentage, count: 1 });
      return;
    }
    byMonth.set(point.month, {
      total: existing.total + point.mix_percentage,
      count: existing.count + 1,
    });
  });

  return [...byMonth.entries()]
    .sort(([monthA], [monthB]) => monthA.localeCompare(monthB))
    .map(([month, { total, count }]) => ({
      month,
      mix_percentage: total / count,
    }));
};

export const useTenantShredUsage = (tenantNames: string[]) => {
  const primaryTenant = tenantNames[0] ?? "";
  const secondaryTenant = tenantNames[1] ?? "";

  const { data: primaryData, isLoading: primaryLoading } =
    useGetMissionControlTenantShredUsageQuery(
      { tenantName: primaryTenant },
      { skip: primaryTenant.length === 0 }
    );
  const { data: secondaryData, isLoading: secondaryLoading } =
    useGetMissionControlTenantShredUsageQuery(
      { tenantName: secondaryTenant },
      { skip: secondaryTenant.length === 0 }
    );

  const isLoading = primaryLoading || secondaryLoading;

  const shredUsage = useMemo(() => {
    const series = [
      primaryData?.shred_usage ?? [],
      secondaryData?.shred_usage ?? [],
    ].filter((points) => points.length > 0) as ShredUsagePoint[][];
    return aggregateShredUsage(series);
  }, [primaryData, secondaryData]);

  const hasShredUsageData = shredUsage.length > 0;

  const latestShredUsage = useMemo(() => {
    if (!hasShredUsageData) return null;
    return shredUsage[shredUsage.length - 1];
  }, [hasShredUsageData, shredUsage]);

  const shredUsageDataPoints = useMemo((): ShredUsageDataPoint[] => {
    if (!hasShredUsageData) return [];
    return shredUsage.map((s) => ({
      value: s.mix_percentage,
      label: dayjs(s.month).format("MMM YYYY"),
    }));
  }, [hasShredUsageData, shredUsage]);

  const formattedMonth = useMemo(() => {
    if (!latestShredUsage) return "";
    return dayjs(latestShredUsage.month).format("MMM YYYY");
  }, [latestShredUsage]);

  return {
    isLoading,
    hasShredUsageData,
    latestShredUsage,
    shredUsageDataPoints,
    formattedMonth,
  };
};
